import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AgentSimulator } from './AgentSimulator';
import { searchMarkets } from '../../services/marketService';
import { MARKETS_DATA } from '../../data/constants';
import { Loader2, Sparkles, Zap } from 'lucide-react';

export const SimulatorView = ({ onBack }) => {
    const location = useLocation();
    const [markets, setMarkets] = useState([]);
    const [selectedMarket, setSelectedMarket] = useState(null);
    const [loading, setLoading] = useState(true);

    // Get query and prediction result from navigation state
    const predictionResult = location.state?.predictionResult;
    const userQuery = location.state?.query || predictionResult?.analysis?.market || '';

    useEffect(() => {
        const loadMarkets = async () => {
            setLoading(true);
            try {
                if (userQuery) {
                    // Fetch and filter markets by query
                    const filteredMarkets = await searchMarkets(userQuery);
                    setMarkets(filteredMarkets);
                    // Auto-select the most relevant market
                    if (filteredMarkets.length > 0) {
                        setSelectedMarket(filteredMarkets[0]);
                    }
                } else {
                    // Fallback to default markets
                    setMarkets(MARKETS_DATA);
                    setSelectedMarket(MARKETS_DATA[0]);
                }
            } catch (error) {
                console.error('Failed to load markets:', error);
                setMarkets(MARKETS_DATA);
                setSelectedMarket(MARKETS_DATA[0]);
            }
            setLoading(false);
        };

        loadMarkets();
    }, [userQuery]);

    return (
        <div className="flex h-[calc(100vh)] overflow-hidden bg-background pt-0">
            {/* Sidebar - Market Feed (Desktop Only) */}
            <aside className="w-80 border-r border-white/10 bg-[#050505] hidden lg:flex flex-col pt-20">
                <div className="p-4 border-b border-white/10">
                    <h3 className="font-bold text-red-500 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        Live Opportunities
                    </h3>
                    {userQuery && (
                        <p className="text-[10px] text-muted-foreground mt-1 truncate">
                            Filtered by: "{userQuery}"
                        </p>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        </div>
                    ) : (
                        markets.map(market => (
                            <div
                                key={market.id}
                                onClick={() => setSelectedMarket(market)}
                                className={`p-3 rounded-lg border cursor-pointer transition-all relative ${selectedMarket?.id === market.id
                                    ? 'bg-red-900/10 border-red-500/50'
                                    : 'bg-white/5 border-white/5 hover:border-white/20'
                                    }`}
                            >
                                {market.isRecommended && (
                                    <div className="absolute -top-1 -right-1 bg-secondary text-black text-[8px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                        <Sparkles className="w-2 h-2" />
                                        MATCH
                                    </div>
                                )}
                                <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">{market.category}</div>
                                <div className="text-sm font-bold text-gray-200 line-clamp-2 leading-snug mb-2">{market.title}</div>
                                <div className="flex justify-between text-xs font-mono">
                                    <span className="text-emerald-400">L: {market.long}%</span>
                                    <span className="text-red-400">S: {market.short}%</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Prediction Result Summary */}
                {predictionResult?.analysis && (
                    <div className="p-3 border-t border-white/10 bg-secondary/5">
                        <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-4 h-4 text-secondary" />
                            <span className="text-[10px] font-bold text-secondary uppercase">AI Insight</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Signal</span>
                            <span className="text-white font-bold">{predictionResult.analysis.signal}</span>
                        </div>
                        <div className="flex justify-between text-xs mt-1">
                            <span className="text-muted-foreground">Confidence</span>
                            <span className="text-secondary font-bold">{predictionResult.analysis.confidence}%</span>
                        </div>
                    </div>
                )}

                <div className="p-4 border-t border-white/10 bg-black">
                    <button onClick={onBack} className="w-full py-2 text-sm text-gray-400 hover:text-white transition-colors border border-white/10 rounded hover:bg-white/5">
                        ← Exit Simulator
                    </button>
                </div>
            </aside>

            {/* Main Area - Simulator */}
            <main className="flex-1 relative flex flex-col pt-16 md:pt-20 bg-black">
                {/* Mobile Market Selector */}
                <div className="lg:hidden absolute top-16 md:top-20 left-0 right-0 z-20 px-4 py-2 bg-black/95 border-b border-white/10 backdrop-blur">
                    <div className="flex items-center gap-2">
                        <button onClick={onBack} className="text-sm text-white bg-white/10 px-3 py-2 rounded border border-white/10 hover:bg-white/20">
                            ← Back
                        </button>
                        <select
                            value={selectedMarket?.id || ''}
                            onChange={(e) => {
                                const market = markets.find(m => String(m.id) === e.target.value);
                                setSelectedMarket(market);
                            }}
                            className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
                        >
                            {markets.map(market => (
                                <option key={market.id} value={market.id} className="bg-black">
                                    {market.isRecommended ? '⭐ ' : ''}{market.category}: {market.title.substring(0, 40)}...
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
                            <p className="text-muted-foreground text-sm">Loading Live Opportunities...</p>
                        </div>
                    </div>
                ) : selectedMarket ? (
                    <AgentSimulator market={selectedMarket} predictionResult={predictionResult} />
                ) : (
                    <div className="flex-1 flex items-center justify-center text-muted-foreground">
                        Select a market to initialize Lobster nodes...
                    </div>
                )}
            </main>
        </div>
    );
};
